import React from "react";
import { useParams } from "react-router-dom";
//#region types
import { EntityIdParams } from "models/propTypes/urlParamsTypes";
import { SettingsConstants } from "utils/constants";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
//#region components
import {
  BreadCrumb,
  PageLayouts,
  Skeletons,
  ViewPanel,
  SimpleEntityTitle,
  SettingsFormViewPanel,
} from "components/common";
import { IconDefinitionConstants as Icons } from "utils/constants";
import { NavMenuItem } from "models/propTypes/pageLayoutPropTypes";
import { ErrorView } from "components/error";
import { EmployeeFormContainer, useEmployeeHelpers } from "components/employee";
import { useEmployeeService } from "serviceHooks";
import useSalaryPermissions from "components/salary/hooks/useSalaryPermissions";
//#endregion
import { LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import clsx from "clsx";
import cssExports from "theme/styles/main.module.scss";

const EmployeeSettings: React.FC = () => {
  const employeeService = useEmployeeService();
  const [employee, setEmployee] = React.useState<WintSuperkollAPIModel.ExtendedEmployee>();
  const [loading, setLoading] = React.useState<boolean>(true);

  const urlParams = useParams<EntityIdParams>();
  const id = urlParams.id ? Number.parseInt(urlParams.id) : undefined;
  const {
    isAdmin,
    isSalaryAdmin,
    superkollSalaryType,
    canView,
    canCreate,
    loggedInUserIsBusinessUnitManagerWithSalaryRights,
  } = useEmployeeHelpers();
  const { isWintSalarySettingsAvailable } = useSalaryPermissions();

  const loadEmployee = () => {
    if (id) {
      employeeService.getEmployee(id).then((emp) => {
        setEmployee(emp);
        setLoading(false);
      });
    } else {
      setEmployee({ salarySettings: { employmentRate: 100 } } as WintSuperkollAPIModel.ExtendedEmployee);
      setLoading(false);
    }
  };

  const upperLeftComponent = React.useMemo(
    () => (
      <SimpleEntityTitle
        title={employee?.name ?? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.NewEmployee)}
      />
    ),
    [employee?.name]
  );

  React.useEffect(() => {
    loadEmployee();
  }, [id]); // Refetch when employee ID changes

  const navItems = React.useMemo((): NavMenuItem[] => {
    return [
      {
        navKey: "employee",
        content: (
          <SettingsFormViewPanel>
            <EmployeeFormContainer
              employee={employee}
              isAdmin={isAdmin}
              isSalaryAdmin={isSalaryAdmin}
              superkollSalaryType={superkollSalaryType}
              isBusinessUnitManagerWithSalaryRights={loggedInUserIsBusinessUnitManagerWithSalaryRights}
            />
          </SettingsFormViewPanel>
        ),
        menuSectionHeader: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.EmployeeSettings),
        icon: Icons.WSend,
      },
    ];
  }, [employee, isAdmin, isSalaryAdmin, superkollSalaryType, loggedInUserIsBusinessUnitManagerWithSalaryRights]);

  return (!id && canCreate) || (id && canView(id)) ? (
    <PageLayouts.EntityFullScreen
      entityKey={`employee`}
      id={1}
      navigation={
        <BreadCrumb
          prevItems={[
            {
              text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Settings),
              href: UrlHelper.formatPath("/Settings"),
            },
            {
              text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Users),
              href: UrlHelper.formatPath(`/Settings?section=${SettingsConstants.NavigationKey.Employees}`),
            },
          ]}
          current={
            employee?.name ?? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.NewEmployee)
          }
          showHome
        />
      }
    >
      <PageLayouts.EntityCreationLayout
        className={clsx(cssExports.userSettingsWizard, id && cssExports.isEditing)}
        upperLeftComponent={upperLeftComponent}
        upperRightComponent={<></>}
      >
        {loading ? (
          <ViewPanel>
            <Skeletons.FormSkeleton />
          </ViewPanel>
        ) : (
          <PageLayouts.NavigationLayout
            navItems={navItems}
            enableDelayedScrollToTarget
            hideNavOnNarrowScreens={isWintSalarySettingsAvailable}
          />
        )}
      </PageLayouts.EntityCreationLayout>
    </PageLayouts.EntityFullScreen>
  ) : (
    <ErrorView></ErrorView>
  );
};

export default EmployeeSettings;
